const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const colorDistance = (data, offset, color) => {
  // Weighted RGB distance: green differences are more visible to the eye.
  const red = data[offset] - color[0];
  const green = data[offset + 1] - color[1];
  const blue = data[offset + 2] - color[2];
  return Math.sqrt(red * red * 0.3 + green * green * 0.59 + blue * blue * 0.11);
};

const borderOffsets = (width, height) => {
  const offsets = [];
  for (let x = 0; x < width; x += 1) {
    offsets.push(x * 4, ((height - 1) * width + x) * 4);
  }
  for (let y = 1; y < height - 1; y += 1) {
    offsets.push(y * width * 4, (y * width + width - 1) * 4);
  }
  return offsets;
};

const dominantBorderColor = (data, offsets) => {
  const buckets = new Map();
  offsets.forEach((offset) => {
    if (data[offset + 3] < 16) return;
    const key = `${data[offset] >> 4},${data[offset + 1] >> 4},${data[offset + 2] >> 4}`;
    const bucket = buckets.get(key) || { count: 0, red: 0, green: 0, blue: 0 };
    bucket.count += 1;
    bucket.red += data[offset];
    bucket.green += data[offset + 1];
    bucket.blue += data[offset + 2];
    buckets.set(key, bucket);
  });
  const winner = [...buckets.values()].sort((a, b) => b.count - a.count)[0];
  if (!winner) return [255, 255, 255];
  return [winner.red / winner.count, winner.green / winner.count, winner.blue / winner.count];
};

/**
 * Removes the border-connected background from raw RGBA pixels. Pixels that
 * merely share the background colour but are enclosed by the subject remain.
 * The returned pixels retain their original RGB values and existing alpha.
 */
export const removeBorderBackground = (source, width, height) => {
  const data = new Uint8ClampedArray(source);
  if (!width || !height || data.length !== width * height * 4) return data;
  const border = borderOffsets(width, height);
  const background = dominantBorderColor(data, border);
  const deviations = border
    .filter((offset) => data[offset + 3] >= 16)
    .map((offset) => colorDistance(data, offset, background))
    .sort((a, b) => a - b);
  const typicalDeviation = deviations[Math.floor(deviations.length * 0.8)] || 0;
  const solidLimit = clamp(typicalDeviation + 18, 22, 58);
  const featherLimit = solidLimit + 24;
  const visited = new Uint8Array(width * height);
  const queue = new Int32Array(width * height);
  let head = 0;
  let tail = 0;

  const enqueue = (index) => {
    if (visited[index]) return;
    const offset = index * 4;
    if (data[offset + 3] === 0 || colorDistance(data, offset, background) <= featherLimit) {
      visited[index] = 1;
      queue[tail++] = index;
    }
  };
  for (let x = 0; x < width; x += 1) {
    enqueue(x);
    enqueue((height - 1) * width + x);
  }
  for (let y = 1; y < height - 1; y += 1) {
    enqueue(y * width);
    enqueue(y * width + width - 1);
  }
  while (head < tail) {
    const index = queue[head++];
    const x = index % width;
    const y = Math.floor(index / width);
    if (x > 0) enqueue(index - 1);
    if (x + 1 < width) enqueue(index + 1);
    if (y > 0) enqueue(index - width);
    if (y + 1 < height) enqueue(index + width);
  }

  for (let index = 0; index < visited.length; index += 1) {
    if (!visited[index]) continue;
    const offset = index * 4;
    const distance = colorDistance(data, offset, background);
    const coverage = clamp((distance - solidLimit) / (featherLimit - solidLimit), 0, 1);
    data[offset + 3] = Math.round(data[offset + 3] * coverage);
  }
  return data;
};

export const removeImageBackground = (image, maxSide = 1600) => new Promise((resolve, reject) => {
  try {
    const naturalWidth = image.naturalWidth || image.width;
    const naturalHeight = image.naturalHeight || image.height;
    const scale = Math.min(1, maxSide / Math.max(naturalWidth, naturalHeight));
    const width = Math.max(1, Math.round(naturalWidth * scale));
    const height = Math.max(1, Math.round(naturalHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    context.drawImage(image, 0, 0, width, height);
    const imageData = context.getImageData(0, 0, width, height);
    imageData.data.set(removeBorderBackground(imageData.data, width, height));
    context.putImageData(imageData, 0, 0);
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("BACKGROUND_REMOVAL_FAILED")), "image/png");
  } catch (error) {
    reject(error);
  }
});
